// Credits: every recording and photograph (fetched from the generated JSON at runtime), the data sources and
// the disclaimers (exact wording from the design spec). Same-origin fetches only; loading, empty and error states.
import type { AppContext, ScreenHandle } from './app';
import type { AssetCredit } from '../types';
import { DISCLAIMERS, HONESTY_LINE, DATA_SOURCES, PHOTO_CREDITS_URL, AUDIO_CREDITS_URL, parseCredits } from '../data/credits';
import { h, clear, button, link } from './dom';

type Fetched = { state: 'loading' } | { state: 'ok'; items: AssetCredit[] } | { state: 'error'; message: string };

async function fetchCredits(url: string): Promise<Fetched> {
  try {
    const res = await fetch(url, { cache: 'force-cache' });
    if (!res.ok) return { state: 'error', message: `${url} answered ${res.status}` };
    return { state: 'ok', items: parseCredits(await res.json()) };
  } catch (e) {
    return { state: 'error', message: e instanceof Error ? e.message : 'fetch failed' };
  }
}

export function mountCredits(root: HTMLElement, ctx: AppContext): ScreenHandle {
  const cache = new Map<string, Fetched>();
  const photos = h('div.panel');
  const audio = h('div.panel');
  const sources = h('div.panel');
  const legal = h('div.panel');
  root.append(
    h('div.wrap', {},
      h('header.topbar', {},
        button('← Hangar', () => ctx.go('hangar'), { cls: 'btn btn-quiet', ariaLabel: 'Back to the hangar' }),
        h('div.title', {}, h('h1', { id: 'credits-title' }, 'Credits'), h('p', {}, HONESTY_LINE)),
      ),
      legal, photos, audio, sources,
    ),
  );

  function section(panel: HTMLElement, heading: string, f: Fetched, empty: string): void {
    clear(panel);
    panel.append(h('h2', {}, heading));
    if (f.state === 'loading') { panel.append(h('p.state', {}, 'Loading…')); return; }
    if (f.state === 'error') { panel.append(h('p.notice.notice-warn', {}, `Could not load this list (${f.message}). It is part of the app, not a network service; reload to retry.`)); return; }
    if (!f.items.length) { panel.append(h('p.state', {}, empty)); return; }
    const ul = h('ul.list');
    for (const c of f.items) {
      ul.append(h('li', {},
        h('div', {}, link(c.title, c.url), ' — ', c.author),
        h('div.small.muted', {}, 'Licence: ', link(c.licence, c.licenceUrl), ' · ', c.file),
      ));
    }
    panel.append(ul);
  }

  async function load(url: string, panel: HTMLElement, heading: string, empty: string): Promise<void> {
    const cached = cache.get(url);
    if (cached && cached.state === 'ok') { section(panel, heading, cached, empty); return; }
    section(panel, heading, { state: 'loading' }, empty);
    const f = await fetchCredits(url);
    cache.set(url, f);
    section(panel, heading, f, empty);
  }

  function render(): void {
    clear(legal); clear(sources);
    legal.append(h('h2', {}, 'Disclaimers'), ...DISCLAIMERS.map((d) => h('p', { style: 'margin-top:.5rem' }, d)));
    sources.append(h('h2', {}, 'Data sources'), h('p.muted.small', {}, 'Every engine, airframe, hull and modification number on the spec cards is taken from one of these.'));
    const ul = h('ul.list');
    for (const s of DATA_SOURCES) ul.append(h('li', {}, link(s.label, s.url)));
    sources.append(ul);
    void load(PHOTO_CREDITS_URL, photos, 'Photographs', 'No photographs are credited yet.');
    void load(AUDIO_CREDITS_URL, audio, 'Engine recordings', 'No recordings are credited yet.');
  }

  return { show: render };
}
